import {
  AppBar,
  Toolbar,
  Box,
  Tooltip,
  Avatar,
  CircularProgress,
  MenuItem,
  Menu,
  IconButton,
  Divider,
  Skeleton,
  Link as Linked,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import ButtonComp from "../../atoms/button/Button";
import HeaderHoverableLogo from "../../molecules/header/HeaderHoverableLogo";
import HeaderButtonGroup from "../../molecules/header/HeaderButtonGroup";
import { fetchUserDetails } from "../../../functions/fetchUserDetails";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { db } from "../../../../backend/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";


import user from "../../../../assets/user.png";
import Icon from "../../atoms/icon/Icon";

import "./styles.css";

const HomepageHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [isSignInLoading, setIsSignInLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // handle user info fetch
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    return () => {
      details && details();
    };
  }, []);

  useEffect(() => {
    const auth = getAuth();
    
    // local variable to hold possible fetchUserDetails unsubscribe
    let authFetchUnsubscribe = null;

    // subscribe to auth state changes (fast)
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Reload user to get latest email verification status
        try {
          await firebaseUser.reload();
          const verified = firebaseUser.emailVerified;
          
          // Check Firestore for mobile-only users and email verification status
          let isMobileOnly = false;
          let firestoreEmailVerified = false;
          try {
            const userRef = doc(db, "users", firebaseUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              isMobileOnly = userData.emailVerified === "MobileOnly" || 
                            firebaseUser.email?.includes("@mobileuser.dentalapp.com");
              firestoreEmailVerified = userData.emailVerified === "Yes";
            }
          } catch (firestoreError) {
            // If Firestore check fails, fall back to email pattern check
            isMobileOnly = firebaseUser.email?.includes("@mobileuser.dentalapp.com");
          }
          
          // Set email verified if: Firebase says verified, OR mobile-only, OR Firestore says verified
          setIsEmailVerified(verified || isMobileOnly || firestoreEmailVerified);
        } catch (error) {
          if (
            error?.code === "auth/user-token-expired" ||
            error?.code === "auth/user-disabled" ||
            error?.code === "permission-denied"
          ) {
            const authInstance = getAuth();
            try {
              if (authInstance.currentUser) {
                await signOut(authInstance);
              }
            } catch (signOutError) {
              if (signOutError?.code !== "auth/no-current-user") {
                console.error("Error signing out expired user:", signOutError);
              }
            }
            setUserDetails(null);
            setIsEmailVerified(false);
            setLoadingUser(false);
            navigate("/sign-in", {
              state: {
                from: location.pathname,
                message: "Your session has ended. Please sign in again.",
              },
              replace: true,
            });
            return;
          } else if (error?.code !== "auth/user-token-expired") {
            console.error("Error reloading user:", error);
          }
          setIsEmailVerified(firebaseUser.emailVerified);
        }
        
        // user is signed in -> fetch the full details
        setLoadingUser(true); // still show skeleton while we fetch details
        // fetchUserDetails should return an unsubscribe (if it sets up listeners) or a promise/callback
        const maybeUnsubscribe = fetchUserDetails((details) => {
          // your fetchUserDetails should invoke this callback with user details OR null
          setUserDetails(details || null);
          setLoadingUser(false); // done fetching (either details or no details)
        }, firebaseUser.uid); // optionally pass uid if your function supports it

        // If fetchUserDetails returned an unsubscribe function, keep it
        // We'll clean it up on unmount or onAuth change
        if (typeof maybeUnsubscribe === "function") {
          // store it on closure for cleanup
          // attach to local variable by closure:
          authFetchUnsubscribe = maybeUnsubscribe;
        } else {
          authFetchUnsubscribe = null;
        }
      } else {
        // no user signed in -> stop loading and stay on current public page
        setUserDetails(null);
        setIsEmailVerified(false);
        setLoadingUser(false);
        // ensure any previous user-listener cleanup runs (if you stored one)
        if (typeof authFetchUnsubscribe === "function") {
          authFetchUnsubscribe();
          authFetchUnsubscribe = null;
        }
      }
    });

    return () => {
      // cleanup both listeners
      unsubscribeAuth();
      if (typeof authFetchUnsubscribe === "function") authFetchUnsubscribe();
    };
  }, []);

  // close dropdown automatically on resize above xs
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 600) {
        setMenuOpen(false);
        const input = document.querySelector(".hamburger input");
        if (input) input.checked = false;
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSignInClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSignInLoading(true);
    
    try {
      // If user is signed in but email is not verified, sign them out first
      const auth = getAuth();
      if (auth.currentUser && !isEmailVerified) {
        await signOut(auth);
        // Reset user state
        setUserDetails(null);
        setIsEmailVerified(false);
      }
    } catch (error) {
      console.error("Error signing out:", error);
    }
    
    setTimeout(() => {
      setIsSignInLoading(false);
      navigate("/sign-in");
    }, 500);
  };

  const handleHamburgerToggle = (e) => {
    setMenuOpen(e.target.checked);
  };

  return (
    <AppBar position="sticky">
      <Toolbar
        sx={{
          backgroundColor: "#FEFEFE",
          padding: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1740px",
            margin: "0 auto",
            padding: {
              xs: "0 10px",
              sm: "0 10px",
              md: "0 50px",
              lg: "0 50px",
              xl: "0 50px",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Logo */}
          <HeaderHoverableLogo />

          {/* Desktop links */}
          <HeaderButtonGroup />

          {/* Desktop right-side buttons */}
          <Box
            display={{
              xs: "none",
              sm: "none",
              md: "none",
              lg: "block",
              xl: "block",
            }}
          >
            {loadingUser ? (
            <Skeleton 
              variant="circular"
              width={50}
              height={50}
            />
          ) : userDetails && isEmailVerified ? (
            <Tooltip title="Dashboard" arrow>
              <Link to="/dashboard">
                <Avatar
                  alt="Profile"
                  sx={{
                    width: "50px",
                    height: "50px",
                    border: "3px solid #59544A",
                    backgroundColor: "#2C251E",
                  }}
                  src={userDetails?.profilePictureUrl || ""}
                >
                  {!userDetails?.profilePictureUrl && (
                    <Icon src={user} size="small" />
                  )}
                </Avatar>
              </Link>
            </Tooltip>
          ) : (
            <ButtonComp
                variant="contained"
                noElevate
                noRipple
                label={
                  isSignInLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                      <span>Sign In</span>
                    </Box>
                  ) : (
                    "Sign In"
                  )
                }
                color="light"
                background="default"
                font="default"
                weight="default"
                border="default"
                selected
                hoverTextColor="light"
                hoverBackground="dark"
                buttonId="headerSignIn"
                clickPass={handleSignInClick}
                open={true}
              />
          )}
          </Box>

          {/* Mobile hamburger */}
          <Tooltip title="Menu" arrow>
            <Box
              display={{
                xs: "flex",
                sm: "flex",
                md: "flex",
                lg: "none",
                xl: "none",
              }}
            >
              <label className="hamburger">
                <input type="checkbox" onChange={handleHamburgerToggle} />
                <svg viewBox="0 0 32 32">
                  <path
                    className="line line-top-bottom"
                    d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
                  ></path>
                  <path className="line" d="M7 16 27 16"></path>
                </svg>
              </label>
            </Box>
          </Tooltip>

          {/* Dropdown Menu (mobile only) */}
          {menuOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "82px",
                right: 0,
                width: "100%",
                backgroundColor: "#FEFEFE",
                boxShadow: "0 5px 8px rgba(0,0,0,0.5)",
                borderRadius: "0 0 16px 16px",
                display: "flex",
                flexDirection: "column",
                zIndex: 9999,
              }}
            >
              <Linked href="/" sx={{ color: "#2C251E"}} underline="none">
                <MenuItem>
                    Home
                </MenuItem>
              </Linked>
              <Linked href="/services" sx={{ color: "#2C251E"}} underline="none">
                <MenuItem>
                    Services
                </MenuItem>
              </Linked>
              <Linked href="/about" sx={{ color: "#2C251E"}} underline="none">
                <MenuItem>
                    About
                </MenuItem>
              </Linked>
              <Linked href="/contact" sx={{ color: "#2C251E"}} underline="none">
                <MenuItem>
                    Contact
                </MenuItem>
              </Linked>
              <Divider/>
              <MenuItem
                onClick={() => {
                  navigate(userDetails && isEmailVerified ? "/dashboard" : "/sign-in");
                  setMenuOpen(false);
                }}
                sx={{
                    pt: "10px",
                    pb: "10px",
                  ":hover": {
                    backgroundColor: "#C5A572",
                    
                    borderRadius: "0 0 16px 16px",
                  }
                }}
              >
                {userDetails && isEmailVerified ? "Dashboard" : "Sign In"}
              </MenuItem>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default HomepageHeader;
